import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ClipboardList, Pencil, Pill, TriangleAlert } from 'lucide-react';
import DoctorShell from '../components/DoctorShell';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  doctorNotePath,
  doctorPatientEditPath,
  ROUTES,
} from '../../constants/routes';
import { doctorGetPatient, doctorPatientNotes } from '../services/doctorApi';

const NOTE_TYPE_LABELS = {
  first_visit: 'First Visit',
  follow_up: 'Follow-up',
  procedure: 'Procedure',
  emergency: 'Emergency',
};

function InfoCard({ title, body, danger, icon: Icon }) {
  const [open, setOpen] = useState(false);
  const empty = !body;
  const accent = danger && !empty;
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className={`rounded-xl px-3 py-3 text-left shadow-sm ring-1 ${
        accent ? 'bg-red-50 ring-red-200' : 'bg-white ring-gray-100'
      }`}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        {Icon && (
          <Icon
            className={`h-3.5 w-3.5 shrink-0 ${accent ? 'text-red-600' : 'text-primary'}`}
            strokeWidth={2}
          />
        )}
        <p className={`text-xs font-semibold ${accent ? 'text-red-700' : 'text-gray-500'}`}>
          {title}
        </p>
      </div>
      <p className={`text-sm ${empty ? 'text-gray-400' : 'text-slate-800'}`}>
        {open || empty
          ? body || '—'
          : `${String(body).slice(0, 48)}${String(body).length > 48 ? '…' : ''}`}
      </p>
    </button>
  );
}

export default function DoctorPatientDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [patient, setPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([doctorGetPatient(id), doctorPatientNotes(id)])
      .then(([p, n]) => {
        if (cancelled) return;
        setPatient(p);
        setNotes(n.notes || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-sm text-critical">
        {error}
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-400">…</div>
    );
  }

  const lastVisit = patient.last_visit_at
    ? new Date(patient.last_visit_at).toLocaleDateString(language === 'ar' ? 'ar-JO' : 'en-GB')
    : t('doctorNoVisits');

  return (
    <DoctorShell showMic micPatientId={id} micPatientLabel={patient.full_name}>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => navigate(ROUTES.DOCTOR_PATIENTS)}>
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <button type="button" onClick={() => navigate(doctorPatientEditPath(id))}>
            <Pencil className="h-5 w-5 text-primary" />
          </button>
        </div>
        <h1 className="mt-2 text-xl font-bold text-slate-900">{patient.full_name}</h1>
        <p className="text-sm text-gray-500">
          {[patient.age, patient.gender].filter(Boolean).join(' · ') || '—'}
          {' · '}
          {lastVisit}
        </p>
      </header>

      <div className="space-y-4 px-4 py-4">
        <div className="grid grid-cols-3 gap-2">
          <InfoCard
            title={t('doctorKnownConditions')}
            body={patient.known_conditions}
            icon={ClipboardList}
          />
          <InfoCard
            title={t('doctorAllergies')}
            body={patient.allergies}
            danger
            icon={TriangleAlert}
          />
          <InfoCard
            title={t('doctorCurrentMeds')}
            body={(patient.current_medications || [])
              .map((m) => m.name)
              .filter(Boolean)
              .join(', ')}
            icon={Pill}
          />
        </div>

        <section>
          <h2 className="mb-2 text-base font-semibold text-slate-900">{t('doctorPreviousVisits')}</h2>
          {notes.length === 0 ? (
            <p className="rounded-xl bg-white px-4 py-6 text-center text-sm text-gray-500 ring-1 ring-gray-100">
              {t('doctorNoPreviousVisits')}
            </p>
          ) : (
            <div className="space-y-2">
              {notes.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => navigate(doctorNotePath(n.id))}
                  className="flex w-full items-center justify-between rounded-xl bg-white px-4 py-3 text-left ring-1 ring-gray-100"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {n.created_at
                        ? new Date(n.created_at).toLocaleDateString(
                            language === 'ar' ? 'ar-JO' : 'en-GB'
                          )
                        : '—'}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary-light px-2 py-0.5 text-[11px] font-semibold text-primary">
                        {NOTE_TYPE_LABELS[n.note_type] || n.note_type}
                      </span>
                      {n.diagnosis_preview && (
                        <span className="text-xs text-gray-500">{n.diagnosis_preview}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </DoctorShell>
  );
}
